import { Button, Input, Textarea } from '../components/admin/ui';
import { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  X,
  Loader2,
  Key,
  Mail,
  Globe,
  Copy,
  Eye,
  EyeOff,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, ApiKey } from '../lib/admin-api';
import { supabase } from '../lib/supabase';

export function ChannelsPage() {
  const [activeTab, setActiveTab] = useState<'api' | 'email'>('api');

  return (
    <div className="support-page">
      <div>
        <h1 className="support-page-title">Channels</h1>
        <p className="text-muted-foreground mt-1">
          Configure API access and email integration
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
        <Button  variant="ghost"
          onClick={() => setActiveTab('api')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'api'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <span className="flex items-center gap-2">
            <Key className="w-4 h-4" />
            API Keys
          </span>
        </Button>
        <Button  variant="ghost"
          onClick={() => setActiveTab('email')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'email'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <span className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email
          </span>
        </Button>
      </div>

      {activeTab === 'api' && <ApiKeysSection />}
      {activeTab === 'email' && <EmailSection />}
    </div>
  );
}

function ApiKeysSection() {
  const { profile } = useAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);

  useEffect(() => {
    fetchKeys();
  }, [profile?.organization_id]);

  async function fetchKeys() {
    if (!profile?.organization_id) return;
    setLoading(true);
    const data = await adminApi.getApiKeys(profile.organization_id);
    setKeys(data);
    setLoading(false);
  }

  const handleCreate = async () => {
    if (!formName.trim() || !profile?.organization_id || !profile?.id) return;
    setSaving(true);

    const result = await adminApi.createApiKey(profile.organization_id, profile.id, formName.trim());

    if (result) {
      setNewKey(result.rawKey);
      fetchKeys();
    }

    setSaving(false);
    setFormName('');
    setShowForm(false);
  };

  const handleRevoke = async (id: string) => {
    if (!confirm('Revoke this API key? This cannot be undone.')) return;
    await adminApi.revokeApiKey(id);
    fetchKeys();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Permanently delete this API key?')) return;
    await adminApi.deleteApiKey(id);
    fetchKeys();
  };

  return (
    <div className="space-y-4">
      {/* New key warning */}
      {newKey && (
        <div className="p-4 bg-warning border border-border rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-warning-foreground flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-warning-foreground">
                Copy your API key now — it won't be shown again
              </p>
              <div className="flex items-center gap-2 mt-2">
                <code className="text-sm bg-card px-3 py-1.5 rounded border border-border font-mono">
                  {newKey}
                </code>
                <Button aria-label="Copy API key" size="iconSm" variant="ghost"
                  onClick={() => {
                    navigator.clipboard.writeText(newKey);
                  }}
                  className="text-warning-foreground hover:text-warning-foreground"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <Button variant="ghost"
                onClick={() => setNewKey(null)}
                className="text-sm text-warning-foreground hover:text-warning-foreground mt-2"
              >
                I've copied it, dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">API Keys</h2>
        <Button variant="ghost"
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          New Key
        </Button>
      </div>

      {showForm && (
        <div className="flex items-center gap-3 bg-card p-4 rounded-lg border border-border">
          <Input
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="Key name (e.g., Mobile App)"
            className="flex-1 border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring"
            autoFocus
          />
          <Button variant="ghost"
            onClick={handleCreate}
            disabled={saving || !formName.trim()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary disabled:bg-primary text-sm font-medium"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
          </Button>
          <Button aria-label="Close" size="iconSm" variant="ghost" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-muted-foreground">
            <X className="w-5 h-5" />
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-link" />
        </div>
      ) : keys.length === 0 ? (
        <div className="text-center py-8 bg-card rounded-lg border border-border">
          <Key className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No API keys</p>
          <p className="text-sm text-muted-foreground">Create a key to use the REST API</p>
        </div>
      ) : (
        <div className="space-y-2">
          {keys.map((key) => (
            <div
              key={key.id}
              className={`bg-card rounded-lg border border-border p-4 ${
                !key.is_active ? 'opacity-50' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">{key.name}</span>
                    {!key.is_active && (
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-destructive/10 text-error-text rounded-full">
                        Revoked
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                    <code className="font-mono">{key.key_prefix}...</code>
                    <span>Created {new Date(key.created_at).toLocaleDateString()}</span>
                    {key.last_used_at && (
                      <span>Last used {new Date(key.last_used_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {key.is_active && (
                    <Button variant="ghost"
                      onClick={() => handleRevoke(key.id)}
                      className="px-3 py-1 text-xs text-error-text hover:bg-destructive/10 rounded transition-colors"
                    >
                      Revoke
                    </Button>
                  )}
                  <Button aria-label="Delete item" size="iconSm" variant="ghost"
                    onClick={() => handleDelete(key.id)}
                    className="p-1.5 text-muted-foreground hover:text-error-text hover:bg-destructive/10 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* API Documentation hint */}
      <div className="bg-muted rounded-lg border border-border p-4">
        <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
          <Globe className="w-4 h-4" />
          API Usage
        </h3>
        <div className="text-xs text-muted-foreground space-y-2 font-mono">
          <p># Create conversation</p>
          <p className="text-foreground">POST /api-channel/conversations</p>
          <p className="text-foreground">X-API-Key: avk_your_key_here</p>
          <p className="mt-3"># Send message</p>
          <p className="text-foreground">{'POST /api-channel/conversations/{id}/messages'}</p>
          <p className="mt-3"># Get conversation with messages</p>
          <p className="text-foreground">{'GET /api-channel/conversations/{id}'}</p>
        </div>
      </div>
    </div>
  );
}

function EmailSection() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [inboundAddress, setInboundAddress] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [signature, setSignature] = useState('');
  const [provider, setProvider] = useState('resend');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, [profile?.organization_id]);

  async function fetchConfig() {
    if (!profile?.organization_id) return;
    setLoading(true);

    const { data: org } = await supabase
      .from('organizations')
      .select('settings')
      .eq('id', profile.organization_id)
      .single();

    if (org?.settings) {
      const emailConfig = (org.settings as Record<string, unknown>).email_channel as Record<string, unknown> | undefined;
      if (emailConfig) {
        setEnabled(!!emailConfig.enabled);
        setInboundAddress((emailConfig.inbound_address as string) || '');
        setFromName((emailConfig.from_name as string) || '');
        setFromAddress((emailConfig.from_address as string) || '');
        setSignature((emailConfig.signature as string) || '');
        setProvider((emailConfig.provider as string) || 'sendgrid');
        setApiKey((emailConfig.api_key as string) || '');
      }
    }

    setLoading(false);
  }

  const [saveError, setSaveError] = useState('');

  const handleSave = async () => {
    if (!profile?.organization_id) return;
    setSaving(true);
    setSaveError('');

    // Fetch current settings
    const { data: org } = await supabase
      .from('organizations')
      .select('settings')
      .eq('id', profile.organization_id)
      .single();

    const currentSettings = (org?.settings || {}) as Record<string, unknown>;

    const { error } = await supabase
      .from('organizations')
      .update({
        settings: {
          ...currentSettings,
          email_channel: {
            enabled,
            inbound_address: inboundAddress,
            from_name: fromName,
            from_address: fromAddress,
            signature,
            provider,
            api_key: apiKey,
          },
        },
      })
      .eq('id', profile.organization_id);

    if (error) {
      setSaveError('Failed to save configuration');
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-link" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Email Channel</h2>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="rounded border-border text-link focus:ring-ring"
            />
            <span className="text-sm text-foreground">Enabled</span>
          </label>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="channelspage-1" className="block text-sm font-medium text-foreground mb-1">Inbound Email Address</label>
              <Input id="channelspage-1"
                type="email"
                value={inboundAddress}
                onChange={(e) => setInboundAddress(e.target.value)}
                placeholder="support@yourdomain.com"
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring"
              />
              <p className="text-xs text-muted-foreground mt-1">Emails sent here create conversations</p>
            </div>
            <div>
              <label htmlFor="channelspage-2" className="block text-sm font-medium text-foreground mb-1">From Name</label>
              <Input id="channelspage-2"
                type="text"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="Support Team"
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div>
            <label htmlFor="channelspage-3" className="block text-sm font-medium text-foreground mb-1">From Address</label>
            <Input id="channelspage-3"
              type="email"
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
              placeholder="noreply@yourdomain.com"
              className="w-full max-w-md border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring"
            />
          </div>

          <div>
            <label htmlFor="channelspage-4" className="block text-sm font-medium text-foreground mb-1">Email Signature</label>
            <Textarea id="channelspage-4"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="--\nYour Company\nwww.example.com"
              rows={3}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="channelspage-5" className="block text-sm font-medium text-foreground mb-1">Email Provider</label>
              <select id="channelspage-5"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring"
              >
                <option value="resend">Resend</option>
                <option value="sendgrid">SendGrid</option>
                <option value="mailgun">Mailgun</option>
                <option value="ses">AWS SES</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Provider API Key</label>
              <div className="relative">
                <Input
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={provider === 'resend' ? 're_xxxx...' : provider === 'sendgrid' ? 'SG.xxxx...' : 'key-xxxx...'}
                  className="w-full border border-border rounded-md px-3 py-2 pr-10 text-sm font-mono focus:ring-2 focus:ring-ring"
                />
                <Button aria-label="Toggle secret visibility" size="iconSm" variant="ghost"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {saveError && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-error-text">
            {saveError}
          </div>
        )}

        <div className="mt-6">
          <Button variant="ghost"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary disabled:bg-primary transition-colors text-sm font-medium"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
}
